import React, { useState, useEffect, useContext } from 'react';
import axios from "@common/axios";
import { Collapse, List, Spin, Alert } from 'antd';
import {
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FilePptOutlined,
    FileOutlined
} from '@ant-design/icons';
import CircularLoader from "@common/CircularLoader";
import { UserContext } from "@context/UserContext";
import {TemplateContext} from "@common/context/TemplateContext";

const { Panel } = Collapse;

const getIcon = (contentType) => {
    switch (contentType) {
        case 'application/pdf':
            return <FilePdfOutlined style={{fontSize: '36px'}} />;
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return <FileWordOutlined style={{fontSize: '36px', color: 'var(--secondary-color)'}} />;
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return <FileExcelOutlined style={{fontSize: '36px', color: 'var(--secondary-color)'}} />;
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            return <FilePptOutlined style={{fontSize: '36px', color: 'var(--secondary-color)'}} />;
        default:
            return <FileOutlined style={{fontSize: '36px', color: 'var(--secondary-color)'}} />;
    }
};

const OfflineResources = (props) => {
    const {resourcesUrl = '', heading = '', collapseDescription, forceHideTitle=false} = props;
    const { user } = useContext(UserContext);
    const {shortcut} = useContext(TemplateContext);
    const [publicProduct, setPublicProduct] = useState(false);
    const [resourceGroups, setResourceGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hideResourcesTitle, setHideResourcesTitle] = useState(true);
    const [error, setError] = useState('');
    const defaultActiveKey = resourceGroups.length === 1 ? String(resourceGroups[0].resourceGroupId) : undefined;

    const fetchResources = () => {
        let selectedUrl = resourcesUrl;
        if(resourcesUrl === '') {
            selectedUrl = shortcut.loadUrl;
        }
        if(selectedUrl && selectedUrl !== ''){
            axios.get(selectedUrl).then(response =>{
                setPublicProduct(response.data.publicProduct || false);
                setResourceGroups(response.data.resourceGroups || []);
            }).catch((err)=>{
                console.error(err);
                setError('Failed to load resources. Please try again later.');
            }).finally(()=>{
                setLoading(false);
            })
        }else{
            console.error('No resourcesUrl');
            setError('Failed to load resources. Please try again later.');
            setLoading(false);
        }

    };

    useEffect(() => {
        if(shortcut) {
            setError('');
            setLoading(true);

            const queryString = window.location.search;
            const params = new URLSearchParams(queryString);

            if(params.get('hideResourcesTitle') == 'true' || forceHideTitle) {
                setHideResourcesTitle(true);
            } else {
                setHideResourcesTitle(false);
            }

            fetchResources();
        }
    }, [shortcut, window.location.search]);

    // Compute if any media is found
    const mediaFound = resourceGroups.some(group =>
        (group.mediaList && group.mediaList.length > 0) ||
        (group.emptyDescription && group.emptyDescription.trim() !== '')
    );

    if (loading) {
        return <CircularLoader />;
    }

    if (error !== '') {
        return (
            <div>
                <Alert message="Error" description={error} type="error" showIcon />
            </div>
        );
    }

    function handleDownloadMedia(media) {
        if (!media) {
            return;
        }

        if (media.mediaType === 'VIDEO') {
            openVideoDialog(media.mediaId);
        } else {
            openMediaDocument(media.mediaId);
        }
    }

    function handleManageMedia(media) {
        if (!media) {
            return;
        }

        var editMediaUrl = '/administration/media/showManageMedia.action?mediaId=' + media.mediaId + '#showMedia';
        document.location.href = editMediaUrl;
    }

    return (
        <div className="ch-offline-resources">
            {!hideResourcesTitle &&
                <h2>{user.syllabusName}</h2>
            }
            {heading &&
                <h3>{heading}</h3>
            }
            {publicProduct && (
                <p className="ch-warning-text">Not all resources are available in sample products.</p>
            )}

            {(!resourceGroups || resourceGroups.length === 0) && !publicProduct && (
                <p className="ch-error-text">This section has no resources.</p>
            )}

            {resourceGroups && resourceGroups.length > 0 && (
                <Collapse
                    key={collapseDescription}
                    accordion={true}
                    bordered={true}
                    size="small"
                    defaultActiveKey={defaultActiveKey}
                >
                    {resourceGroups.map((group) => (
                        <>
                        {group.mediaList && group.mediaList.length > 0 &&
                            <Panel
                                header={
                                    <div className={'smallGap'}>
                                        <span>{group.name}</span>
                                        {group.highlight && group.highlightText && (
                                            <span className="ch-highlight-new">
                                                {group.highlightText}
                                            </span>
                                        )}
                                        {user.showResourceIds && (
                                            <span className="resourceId">
                                                #{group.resourceGroupId}
                                            </span>
                                        )}
                                    </div>
                                }
                                key={group.resourceGroupId}
                            >
                                {(!group.mediaList || group.mediaList.length === 0) && group.emptyDescription ? (
                                    <div style={{paddingBottom: '10px'}}>
                                        <b dangerouslySetInnerHTML={{__html: group.emptyDescription}}/>
                                    </div>
                                ) : (
                                    <>
                                        {group.description && (
                                            <div style={{paddingBottom: '10px'}}>
                                                <span dangerouslySetInnerHTML={{__html: group.description}}/>
                                            </div>
                                        )}
                                        {group.mediaList && group.mediaList.length > 0 ? (
                                            <List
                                                itemLayout="horizontal"
                                                dataSource={group.mediaList}
                                                renderItem={(item) => (
                                                    <List.Item key={item.mediaId}>
                                                        <List.Item.Meta
                                                            avatar={getIcon(item.contentType)}
                                                            title={
                                                                <a className="ch-link" tabIndex={0}
                                                                   onClick={() => handleDownloadMedia(item)}>
                                                                    {item.mediaName || item.downloadFileName}
                                                                </a>
                                                            }
                                                            description={item.mediaDescription || ''}
                                                        />
                                                        {user.showResourceIds && user.adminUser &&
                                                            <a className="resourceId" onClick={() => handleManageMedia(item)}>#{item.mediaId}</a>
                                                        }
                                                        {user.showResourceIds && !user.adminUser &&
                                                            <span className="resourceId">#{item.mediaId}</span>
                                                        }
                                                    </List.Item>
                                                )}
                                            />
                                        ) : (
                                            <p className="ch-error-text">No resources are available.</p>
                                        )}
                                    </>
                                )}
                            </Panel>}
                        </>
                    ))}
                </Collapse>
            )}

            {!mediaFound && !publicProduct && (
                <p className="ch-error-text">This section has no resources.</p>
            )}
        </div>
    );
};

export default OfflineResources;
